import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ShopProvider, useShop } from './context/ShopContext';
import GoldRateTicker from './components/GoldRateTicker';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BottomNav from './components/BottomNav';
import AdminLayout from './components/Admin/AdminLayout';
import JewelCasaChatWidget from './components/JewelCasaChatWidget';
import QuickViewModal from './components/QuickViewModal';
import CartDrawer from './components/CartDrawer';
import WishlistDrawer from './components/WishlistDrawer';
import CheckoutModal from './components/CheckoutModal';
import PolicyModal from './components/PolicyModal';
import PromoPopupModal from './components/PromoPopupModal';
import InstallAppBanner from './components/InstallAppBanner';

// Dedicated Separate Pages
import HomePage from './pages/HomePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoryPage from './pages/CategoryPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import WishlistPage from './pages/WishlistPage';
import PolicyPage from './pages/PolicyPage';
import LiveRatesPage from './pages/LiveRatesPage';
import CalculatorPage from './pages/CalculatorPage';
import StoreLocatorPage from './pages/StoreLocatorPage';
import CustomerDashboardPage from './pages/CustomerDashboardPage';
import B2BClubPage from './pages/B2BClubPage';
import WastageCalculatorPage from './pages/WastageCalculatorPage';
import WastageListPage from './pages/WastageListPage';
import CustomerAuthModal from './components/CustomerAuthModal';
import StickyContactBar from './components/StickyContactBar';

import { CheckCircle2, AlertCircle, Info } from 'lucide-react';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
}

function ToastList() {
  const { toasts } = useShop();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div key={toast.id} className="toast">
          {toast.type === 'success' && <CheckCircle2 size={18} color="#34d399" />}
          {toast.type === 'error' && <AlertCircle size={18} color="#fb7185" />}
          {toast.type === 'info' && <Info size={18} color="var(--gold-400)" />}
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}

function StorefrontApp() {
  const location = useLocation();
  const { 
    isAdminAuthenticated, 
    logoutAdmin,
    isCustomerLoggedIn,
    openCustomerAuthModal
  } = useShop();

  const hasPromptedAuthRef = React.useRef(false);

  // Distinct Route Check: Only render AdminLayout when route is explicitly #/admin or /admin
  const isAdminRoute = 
    location.pathname === '/admin' || 
    location.pathname === 'admin' || 
    (typeof window !== 'undefined' && (window.location.hash === '#admin' || window.location.hash.startsWith('#/admin')));

  // Note: Disabled auto-popup on website open as requested - visitors land directly on the main page.
  // The login modal opens when user clicks "Login" or performs authenticated actions.

  if (isAdminRoute) {
    return (
      <>
        <AdminLayout />
        <ToastList />
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <ScrollToTop />

      {/* Top Floating Admin Status Ribbon when staff is previewing the customer storefront */}
      {isAdminAuthenticated && (
        <div style={{
          backgroundColor: '#0d2346',
          color: '#ffffff',
          padding: '6px 16px',
          fontSize: '0.8rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 229, 0, 0.4)',
          position: 'sticky',
          top: 0,
          zIndex: 99999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#ffe500', fontWeight: 800 }}>👑 Admin Mode Active:</span>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>Viewing customer storefront preview</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <a 
              href="#/admin" 
              style={{
                backgroundColor: '#ffe500',
                color: '#0d2346',
                fontWeight: 700,
                fontSize: '0.76rem',
                padding: '4px 12px',
                borderRadius: '2px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Open Admin Dashboard ⚙️
            </a>
            <button
              onClick={logoutAdmin}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: '#ffffff',
                fontSize: '0.74rem',
                padding: '3px 10px',
                borderRadius: '2px',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {/* 0. Mobile Android PWA One-Tap Install App Banner */}
      <InstallAppBanner />

      {/* 1. Live Gold & Silver Rates Ribbon with JewelCasa Rotating Offers */}
      <GoldRateTicker />

      {/* 2. Luxury Sticky Header Navigation with Separate Page Routing */}
      <Navbar />

      {/* 3. Dedicated Route Pages */}
      <main style={{ flex: 1 }}>
        <Routes>
          {/* Main Functions & Tools Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/live-rates" element={<LiveRatesPage />} />
          <Route path="/calculator" element={<CalculatorPage />} />
          <Route path="/wastage-calculator" element={<WastageCalculatorPage />} />
          <Route path="/wastage-rate-card" element={<WastageListPage />} />
          <Route path="/b2b-club" element={<B2BClubPage />} />
          <Route path="/store-locator" element={<StoreLocatorPage />} />
          <Route path="/contact" element={<StoreLocatorPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/account" element={<CustomerDashboardPage />} />
          <Route path="/customer-dashboard" element={<CustomerDashboardPage />} />
          <Route path="/my-orders" element={<CustomerDashboardPage />} />
          <Route path="/about" element={<PolicyPage />} />
          <Route path="/policy" element={<PolicyPage />} />
          <Route path="/policy/:type" element={<PolicyPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />

          {/* Product Category Pages (Direct & Parameterized URLs) */}
          <Route path="/category/:categoryId" element={<CategoryPage />} />
          <Route path="/bridal" element={<CategoryPage />} />
          <Route path="/rings" element={<CategoryPage />} />
          <Route path="/necklaces" element={<CategoryPage />} />
          <Route path="/earrings" element={<CategoryPage />} />
          <Route path="/bangles" element={<CategoryPage />} />
          <Route path="/kitchenware" element={<CategoryPage />} />
          <Route path="/collections" element={<CategoryPage />} />
          <Route path="/all-jewellery" element={<CategoryPage />} />
          <Route path="/all-jewels" element={<CategoryPage />} />

          {/* Seller / Staff Admin Portal */}
          <Route path="/admin" element={<AdminLayout />} />

          {/* Catch-all fallback */}
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>

      {/* 4. Brand Heritage Footer */}
      <Footer />

      {/* 5. Sticky Mobile App Bottom Bar */}
      <BottomNav />

      {/* 6. Sticky WhatsApp & Call Buttons */}
      <StickyContactBar />

      {/* 7. JewelCasa Floating Concierge CHAT Widget */}
      <JewelCasaChatWidget />

      {/* 7. Drawers & Modals (Available for quick action fallbacks) */}
      <QuickViewModal />
      <CartDrawer />
      <WishlistDrawer />
      <CheckoutModal />
      <PolicyModal />
      <PromoPopupModal />
      <CustomerAuthModal />

      {/* 8. Floating Toast Notifications */}
      <ToastList />
    </div>
  );
}

export default function App() {
  return (
    <HashRouter>
      <ShopProvider>
        <StorefrontApp />
      </ShopProvider>
    </HashRouter>
  );
}
